import type { GeoJsonFeatureCollection, LatLngBounds } from '@gpx-spa/geo-domain'
import { getMapStyleFromFeatureProperties, lineDashPatternToPixelSegments } from '@gpx-spa/geo-domain'
import L from 'leaflet'

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function toLeafletBounds(b: LatLngBounds): L.LatLngBoundsExpression {
  return L.latLngBounds(
    L.latLng(b.southWest.lat, b.southWest.lng),
    L.latLng(b.northEast.lat, b.northEast.lng)
  )
}

export function pathOptionsFromMapStyle(s: ReturnType<typeof getMapStyleFromFeatureProperties>): L.PathOptions {
  return {
    color: s.stroke ?? s.lineColor ?? '#333333',
    weight: s.strokeWidth ?? s.lineWidth ?? 2,
    opacity: s.strokeOpacity ?? s.lineOpacity ?? 1,
    fillColor: s.fill,
    fillOpacity: s.fillOpacity ?? 0.35,
  }
}

function leafletDashArrayStringFromCanonicalPattern(dash: number[], lineWidthPx: number): string {
  return lineDashPatternToPixelSegments(dash, lineWidthPx).join(' ')
}

export function collectionHasLineDashArray(collection: GeoJsonFeatureCollection): boolean {
  for (const f of collection.features) {
    if (f.geometry.type !== 'LineString' && f.geometry.type !== 'MultiLineString') continue
    const m = getMapStyleFromFeatureProperties(f.properties as Record<string, unknown> | undefined)
    if (Array.isArray(m.lineDashArray) && m.lineDashArray.length > 0) return true
  }
  return false
}

export function styleForGeoJsonFeature(feature: GeoJsonFeatureCollection['features'][0]): L.PathOptions {
  const s = getMapStyleFromFeatureProperties(feature.properties as Record<string, unknown> | undefined)
  const geom = feature.geometry
  if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
    return pathOptionsFromMapStyle(s)
  }
  if (geom.type === 'LineString' || geom.type === 'MultiLineString') {
    const dash = s.lineDashArray
    const hasDash = Array.isArray(dash) && dash.length > 0
    const weight = s.lineWidth ?? 4
    return {
      color: s.lineColor ?? '#333333',
      weight,
      opacity: s.lineOpacity ?? 1,
      ...(hasDash
        ? {
            dashArray: leafletDashArrayStringFromCanonicalPattern(dash, weight) as L.PathOptions['dashArray'],
            smoothFactor: 0,
            lineCap: 'round',
            lineJoin: 'round',
          }
        : {}),
    }
  }
  return { color: '#888888', weight: 2 }
}

function tryLeafletNativeCircleLatLngRadius(
  feature: GeoJsonFeatureCollection['features'][0]
): { lat: number; lng: number; radiusM: number } | null {
  const p = feature.properties as Record<string, unknown> | undefined
  if (!p || p.type !== 'circle') return null
  const c = p.center
  const r = p.radius
  if (!Array.isArray(c) || c.length < 2) return null
  const lng = Number(c[0])
  const lat = Number(c[1])
  const radiusM = Number(r)
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(radiusM) || radiusM <= 0) return null
  return { lat, lng, radiusM }
}

export function partitionLeafletNativeCircles(collection: GeoJsonFeatureCollection): {
  nativeCircles: Array<{
    feature: GeoJsonFeatureCollection['features'][0]
    lat: number
    lng: number
    radiusM: number
  }>
  rest: GeoJsonFeatureCollection['features']
} {
  const nativeCircles: Array<{
    feature: GeoJsonFeatureCollection['features'][0]
    lat: number
    lng: number
    radiusM: number
  }> = []
  const rest: GeoJsonFeatureCollection['features'] = []
  for (const f of collection.features) {
    const parsed = tryLeafletNativeCircleLatLngRadius(f)
    if (parsed) {
      nativeCircles.push({ feature: f, ...parsed })
    } else {
      rest.push(f)
    }
  }
  return { nativeCircles, rest }
}

export function partitionAntPathLineFeatures(collection: GeoJsonFeatureCollection): {
  antPathLines: GeoJsonFeatureCollection['features']
  rest: GeoJsonFeatureCollection['features']
} {
  const antPathLines: GeoJsonFeatureCollection['features'] = []
  const rest: GeoJsonFeatureCollection['features'] = []
  for (const f of collection.features) {
    if (f.geometry.type !== 'LineString') {
      rest.push(f)
      continue
    }
    const s = getMapStyleFromFeatureProperties(f.properties as Record<string, unknown> | undefined)
    if (s.lineAntPath) {
      antPathLines.push(f)
    } else {
      rest.push(f)
    }
  }
  return { antPathLines, rest }
}

export function partitionGeodesicLineFeatures(collection: GeoJsonFeatureCollection): {
  geodesicLines: GeoJsonFeatureCollection['features']
  rest: GeoJsonFeatureCollection['features']
} {
  const geodesicLines: GeoJsonFeatureCollection['features'] = []
  const rest: GeoJsonFeatureCollection['features'] = []
  for (const f of collection.features) {
    if (f.geometry.type !== 'LineString') {
      rest.push(f)
      continue
    }
    const s = getMapStyleFromFeatureProperties(f.properties as Record<string, unknown> | undefined)
    const coords = f.geometry.coordinates
    const isTwoPoint = Array.isArray(coords) && coords.length === 2
    if (s.lineGeodesic && !s.lineAntPath && isTwoPoint) {
      geodesicLines.push(f)
    } else {
      rest.push(f)
    }
  }
  return { geodesicLines, rest }
}
